import { ReactElement } from 'react';

import dayjs from 'dayjs';

import { DATE_TIME_WITH_OFFSET } from '@cast/constants';
import {
  Column,
  DataTable,
  Tooltip,
  Typography,
  Box,
  tokens,
  withAlpha,
} from '@cast/design-system';
import {
  OptimizationWorkloadPod,
  WorkloadRecommendationStatusType,
  WorkloadRecommendationStatusTypeEnum,
} from '@cast/types';

import { AMOUNT_OF_PODS_IN_TOOLTIP } from './_constants';
import { RecommendationAppliedCell } from './RecommendationAppliedCell';
import { useCurrentWorkloadMetrics } from '../../../_hooks/useCurrentWorkloadMetrics';

import { NoData } from '@cast/shared-search-ui';

type WorkloadStatusChipTooltipProps = {
  workloadId: string;
  workloadStatus: WorkloadRecommendationStatusType;
  appliedPods: number;
  totalPods: number;
  children: ReactElement;
  stopReasonMessage?: string;
};

type AppliedPodsCellTooltipContentProps = {
  workloadId: string;
  totalPods: number;
  appliedPods: number;
};

const AppliedPodsCellTooltipContent = ({
  workloadId,
  totalPods,
  appliedPods,
}: AppliedPodsCellTooltipContentProps) => {
  const { pods, isLoading } = useCurrentWorkloadMetrics({
    workloadId,
    includeMetrics: false,
    refetchInterval: () => 30_000,
  });

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      data-testid="applied-pods-tooltip"
    >
      <Box
        sx={{
          bgcolor: tokens.theme.bg.status.successSubtle,
          color: tokens.theme.fg.status.onSuccessSubtle,
          border: '1px solid',
          borderColor: tokens.theme.border.structural.neutral,
          borderBottom: 'unset',
          padding: '4px 16px',
          borderRadius: '4px 4px 0px 0px',
        }}
      >
        <Typography variant="P10B" data-testid="applied-pods-tooltip-header">
          APPLIED PODS {appliedPods}/{totalPods}
        </Typography>
      </Box>
      {!isLoading && !pods?.length ? (
        <NoData
          entity="pods"
          hexProps={{
            size: 42,
          }}
          sx={{
            width: 617,
            height: 150,
            marginTop: 0,
            justifyContent: 'center',
          }}
        />
      ) : (
        <DataTable
          testId="applied-pods-tooltip-table"
          data={pods?.slice(0, Math.min(4, pods.length))}
          isLoading={isLoading}
          sx={{
            color: tokens.theme.fg.structural.primaryOnInverse,

            '& .DS-Table-CellWrapper': {
              backgroundColor: tokens.theme.bg.structural.surfaceInverse,
            },

            '& .DS-Table-HeaderCell': {
              fontFamily: 'Space mono',
              fontWeight: 400,
              fontSize: 12,
              letterSpacing: 0,
              color: tokens.theme.fg.structural.secondaryOnInverse,
            },

            '& .DS-Table-BodyCell': {
              fontFamily: 'Space mono',
              fontSize: 12,
              letterSpacing: 0,
              color: tokens.theme.fg.structural.primaryOnInverse,
            },

            '& .DS-Table-HeaderRowWrapper': {
              backgroundColor: tokens.theme.bg.structural.surfaceInverse,
              borderColor: tokens.theme.border.structural.neutral,
            },

            '& .DS-Table-Body': {
              backgroundColor: tokens.theme.bg.structural.surfaceInverse,
            },

            '& .DS-Table-BodyRowWrapper': {
              '&:not(.DS-Table-firstRow)': {
                borderColor: tokens.theme.border.structural.neutral,
              },

              '& .MuiSkeleton-root': {
                background: tokens.theme.fg.structural.secondaryOnInverse,
              },
            },

            '& .DS-Table-BodyRow': {
              '&:hover': {
                backgroundColor: tokens.theme.bg.structural.surfaceInverse,

                '& .DS-Table-CellWrapper': {
                  // selection.neutralHover is a light fill — wrong on inverse surfaces
                  backgroundColor: withAlpha(
                    tokens.theme.fg.structural.primaryOnInverse,
                    0.08
                  ),
                },
              },
            },
          }}
        >
          <Column<OptimizationWorkloadPod>
            id="name"
            header="pod name"
            width={217}
            renderCell={({ name }) => (
              <Typography
                variant="P12R"
                sx={{
                  color: tokens.theme.fg.structural.primaryOnInverse,
                  fontFamily: 'Space mono',
                }}
              >
                {name}
              </Typography>
            )}
          />

          <Column<OptimizationWorkloadPod>
            id="recommendationApplied"
            header="recomm applied"
            width={150}
            renderCell={({
              isLatestRecommendation,
              containers,
              recommendation,
            }) => (
              <RecommendationAppliedCell
                isLatestRecommendation={isLatestRecommendation}
                containers={containers}
                recommendation={recommendation}
              />
            )}
          />

          <Column<OptimizationWorkloadPod>
            id="appliedAt"
            header="timestamp"
            width={250}
            renderCell={({ recommendation }) => (
              <Typography
                variant="P12R"
                sx={{
                  color: tokens.theme.fg.structural.secondaryOnInverse,
                  fontFamily: 'Space mono',
                }}
              >
                {recommendation?.appliedAt
                  ? dayjs(recommendation?.appliedAt).format(
                      DATE_TIME_WITH_OFFSET
                    )
                  : ''}
              </Typography>
            )}
          />
        </DataTable>
      )}
      <Box
        sx={{
          bgcolor: tokens.theme.bg.structural.surfaceInverse,
          padding: '4px 16px',
          borderTop: '1px solid',
          borderColor: tokens.theme.border.structural.neutral,
          borderRadius: '0px 0px 4px 4px',
        }}
      >
        <Typography
          variant="P12B"
          sx={{
            fontWeight: 700,
            color: tokens.theme.fg.structural.primaryOnInverse,
          }}
          data-testid="applied-pods-tooltip-footer"
        >
          {totalPods <= AMOUNT_OF_PODS_IN_TOOLTIP
            ? 'Click on the status to view in drawer'
            : `Click on the status to view all (+${
                totalPods - AMOUNT_OF_PODS_IN_TOOLTIP
              } more)`}
        </Typography>
      </Box>
    </Box>
  );
};

export const WorkloadStatusChipTooltip = ({
  workloadId,
  workloadStatus,
  totalPods,
  appliedPods,
  stopReasonMessage,
  children,
}: WorkloadStatusChipTooltipProps) => {
  if (workloadStatus === WorkloadRecommendationStatusTypeEnum.STOPPED) {
    return (
      <Tooltip title={stopReasonMessage} placement="top" arrow>
        {children}
      </Tooltip>
    );
  }

  return (
    <Tooltip
      wrapIf={workloadStatus === WorkloadRecommendationStatusTypeEnum.APPLIED}
      PopperProps={{
        keepMounted: false,
        sx: {
          maxWidth: 'unset',

          '& .MuiTooltip-tooltip': {
            maxWidth: 'unset',
            padding: 0,
            borderRadius: 4,
          },
        },
      }}
      title={
        <AppliedPodsCellTooltipContent
          appliedPods={appliedPods}
          totalPods={totalPods}
          workloadId={workloadId}
        />
      }
      placement="top"
      arrow
    >
      {children}
    </Tooltip>
  );
};
